import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AuthenticationModal from '../../components/ui/AuthenticationModal';
import PicklingLogoMark from '../../components/PicklingLogoMark';
import ParticipantTable from '../../components/ui/ParticipantTable';
import SessionInfoCard from './components/SessionInfoCard';
import CountdownTimer from './components/CountdownTimer';
import CapacityIndicator from './components/CapacityIndicator';
import RegistrationForm from './components/RegistrationForm';
import PaymentInfoCard from './components/PaymentInfoCard';
import ShareButton from './components/ShareButton';
import CommentSection from './components/CommentSection';
import { supabase } from '../../lib/supabaseClient';

const PublicSessionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const openTarget = searchParams.get('open');
  const isEmbedParam = String(searchParams.get('embed') || '').trim() === '1';
  const isEmbedded = useMemo(() => {
    if (isEmbedParam) return true;
    try {
      return typeof window !== 'undefined' && window.self !== window.top;
    } catch {
      return true;
    }
  }, [isEmbedParam]);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showNewRegistrationModal, setShowNewRegistrationModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionInfo, setCompletionInfo] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelForm, setCancelForm] = useState({ name: '', phone: '' });
  const [isCancelling, setIsCancelling] = useState(false);
  const [showRealNames, setShowRealNames] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [loadError, setLoadError] = useState('');
  const [session, setSession] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [pendingOpen, setPendingOpen] = useState('');

  const normalizeInviteParticipants = (raw) => {
    if (!Array.isArray(raw)) return [];
    return raw
      .map(p => ({
        id: p?.id || globalThis.crypto?.randomUUID?.() || String(Date.now()),
        name: p?.name || '',
        nickname: p?.nickname || p?.name || '',
        phone: p?.phone || '',
        status: p?.status || 'pending',
        payment: p?.payment || 'pending',
        registeredAt: p?.registeredAt || p?.registered_at || new Date().toISOString(),
        skillLevel: p?.skillLevel || p?.skill_level || '',
        guestCount: p?.guestCount ?? p?.guest_count ?? '0'
      }))
      .filter(p => (p?.name || '').trim() && (p?.phone || '').trim());
  };

  const computeCounts = (list) => {
    const active = (list || []).filter(p => p?.status !== 'cancelled');
    const confirmed = active.filter(p => p?.status === 'confirmed')?.length || 0;
    const registered = active.length;
    return { confirmed, waitlist: 0, registered, isFull: false };
  };

  useEffect(() => {
    const loadSessionData = async () => {
      if (!token) {
        setLoadError('잘못된 접근입니다. 초대장 토큰이 없습니다.');
        setSession(null);
        setPaymentInfo(null);
        setParticipants([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError('');
      try {
        const { data: rows, error } = await supabase
          .from('session_invites')
          .select('*')
          .eq('token', token)
          .limit(2);

        if (error) {
          setLoadError(error?.message || '세션 정보를 불러오지 못했습니다.');
          setSession(null);
          setPaymentInfo(null);
          setParticipants([]);
          return;
        }

        const list = Array.isArray(rows) ? rows : [];
        if (list?.length === 0) {
          setLoadError('세션을 불러올 수 없습니다. (유효하지 않은 링크입니다)');
          setSession(null);
          setPaymentInfo(null);
          setParticipants([]);
          return;
        }

        if (list?.length > 1) {
          // Duplicate token exists; proceed with the first row to avoid blocking the user.
        }

        const data = list?.[0];

        const rawType = String(data?.event_type || data?.session_type || data?.type || '').trim().toLowerCase();
        const normalizedType = ['open_game', 'lesson', 'training', 'tournament', 'ad'].includes(rawType)
          ? rawType
          : 'open_game';

        const mappedSession = {
          id: data?.id,
          token: data?.token,
          title: data?.title,
          coverImageUrl: String(data?.cover_image_url || '').trim(),
          imageUrls: Array.isArray(data?.image_urls) ? data.image_urls : [],
          sportIcon: 'CircleDot',
          venue: data?.venue || '',
          venueAddress: data?.venue_address || '',
          location: data?.venue || '',
          date: data?.date ? new Date(data.date).toISOString() : null,
          startTime: data?.start_time,
          endTime: data?.end_time,
          skillRange: '',
          description: String(data?.description || '').trim(),
          registrationDeadline: null,
          capacity: data?.capacity ?? 0,
          confirmed: 0,
          waitlist: 0,
          status: data?.status || 'open',
          eventType: normalizedType,
        };

        const mappedPayment = {
          pricingModel: 'fixed',
          memberPrice: data?.price ?? 0,
          guestPrice: data?.price ?? 0,
          bankName: data?.bank_name || '',
          accountNumber: data?.account_number || '',
          accountHolder: data?.account_holder || '',
          paymentDeadline: data?.payment_deadline || null,
          notes: ''
        };

        const dbParticipants = normalizeInviteParticipants(data?.participants);
        const counts = computeCounts(dbParticipants);

        setSession({
          ...mappedSession,
          confirmed: counts.confirmed,
          waitlist: counts.waitlist
        });
        setPaymentInfo(mappedPayment);
        setParticipants(dbParticipants);
      } catch (err) {
        if (err?.name === 'AbortError') {
          return;
        }

        setLoadError(err?.message || '세션 정보를 불러오는 중 오류가 발생했습니다.');
        setSession(null);
        setPaymentInfo(null);
        setParticipants([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadSessionData();
  }, [token]);

  const sessionImages = useMemo(() => {
    const cover = String(session?.coverImageUrl || '').trim();
    const urls = Array.isArray(session?.imageUrls) ? session.imageUrls : [];
    const normalized = urls
      .map((u) => String(u || '').trim())
      .filter(Boolean);
    const list = cover ? [cover, ...normalized.filter((u) => u !== cover)] : normalized;
    return Array.from(new Set(list));
  }, [session?.coverImageUrl, session?.imageUrls]);

  useEffect(() => {
    setActiveSlideIndex(0);
  }, [token]);

  useEffect(() => {
    const next = String(openTarget || '').trim();
    setPendingOpen(next);
  }, [openTarget]);

  useEffect(() => {
    if (!pendingOpen) return;
    if (!session?.token) return;
    if (showAuthModal || showNewRegistrationModal || showCompletionModal) return;

    if (pendingOpen === 'apply') {
      setPendingOpen('');
      handleOpenRegistration();
      return;
    }

    if (pendingOpen === 'inquiry') {
      setPendingOpen('');
      setTimeout(() => {
        try {
          document.getElementById('public-session-inquiry')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch {
          // ignore
        }
      }, 50);
    }
  }, [pendingOpen, session?.token, showAuthModal, showNewRegistrationModal, showCompletionModal]);

  useEffect(() => {
    const isAnyModalOpen = showAuthModal || showNewRegistrationModal || showCompletionModal;
    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showAuthModal, showNewRegistrationModal, showCompletionModal]);

  const handleQuickAuth = () => {
    setShowAuthModal(true);
  };

  const handleOpenRegistration = () => {
    setShowNewRegistrationModal(true);
  };

  const handleCloseNewRegistrationModal = () => {
    setShowNewRegistrationModal(false);
  };

  const handleOpenCompletionModal = (info) => {
    setCompletionInfo(info || null);
    setShowCompletionModal(true);
  };

  const handleCloseCompletionModal = () => {
    setShowCompletionModal(false);
    setCompletionInfo(null);
  };

  const handleOpenCancelModal = () => {
    setCancelForm({ name: '', phone: '' });
    setShowCancelModal(true);
  };

  const handleCancelSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      alert('잘못된 접근입니다.');
      return;
    }
    const name = String(cancelForm?.name || '').trim();
    const phone = String(cancelForm?.phone || '').replace(/\D/g, '').trim();
    if (!name || !phone) {
      alert('이름과 전화번호를 입력해주세요.');
      return;
    }

    if (!confirm('신청을 취소하시겠습니까?')) return;

    setIsCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke('invite-cancel', {
        body: { token, name, phone },
      });

      if (error) {
        let msg = error?.message || '신청 취소에 실패했습니다.';
        try {
          if (error?.context && typeof error.context?.json === 'function') {
            const errBody = await error.context.json();
            if (errBody?.error) msg = errBody.error;
          }
        } catch {
          // ignore
        }
        throw new Error(msg);
      }

      if (!data?.ok) {
        throw new Error(data?.error || '신청 취소에 실패했습니다.');
      }

      const nextList = normalizeInviteParticipants(data?.participants);
      setParticipants(nextList);

      const nextCounts = computeCounts(nextList);
      setSession((s) => ({
        ...(s || {}),
        confirmed: nextCounts.confirmed,
        waitlist: nextCounts.waitlist,
      }));

      setShowCancelModal(false);
      alert('신청이 취소되었습니다.');
    } catch (err) {
      alert(err?.message || '신청 취소에 실패했습니다.');
    } finally {
      setIsCancelling(false);
    }
  };

  const invokeInviteRegister = async (body) => {
    const { accessToken } = await resolveAuthContext();

    const { data, error } = await supabase.functions.invoke('invite-register', {
      body,
      headers: accessToken
        ? {
            Authorization: `Bearer ${accessToken}`,
          }
        : undefined,
    });

    return { data, error };
  };

  const resolveAuthContext = async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData?.session;
    const initialToken = String(session?.access_token || '').trim();

    if (!initialToken) {
      return { accessToken: '', userId: '', authError: '' };
    }

    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user?.id) {
        return {
          accessToken: '',
          userId: '',
          authError: String(userError?.message || 'invalid-jwt').trim(),
        };
      }

      const { data: refreshedSessionData } = await supabase.auth.getSession();
      const refreshedSession = refreshedSessionData?.session;
      return {
        accessToken: String(refreshedSession?.access_token || initialToken).trim(),
        userId: String(refreshedSession?.user?.id || userData.user.id || '').trim(),
        authError: '',
      };
    } catch (e) {
      return {
        accessToken: '',
        userId: '',
        authError: e instanceof Error ? e.message : 'invalid-jwt',
      };
    }
  };

  const parseFunctionInvokeError = async (error, fallbackMessage) => {
    const fallback = String(fallbackMessage || '요청 처리에 실패했습니다.').trim();
    if (!error) return fallback;

    const status = Number(error?.context?.status);
    const statusText = String(error?.context?.statusText || '').trim();
    let serverMessage = '';

    try {
      if (error?.context && typeof error.context.json === 'function') {
        const body = await error.context.json();
        serverMessage = String(body?.error || body?.message || '').trim();
      }
    } catch {
      // ignore
    }

    if (!serverMessage) {
      try {
        if (error?.context && typeof error.context.text === 'function') {
          const text = String(await error.context.text()).trim();
          if (text) {
            try {
              const parsed = JSON.parse(text);
              serverMessage = String(parsed?.error || parsed?.message || text).trim();
            } catch {
              serverMessage = text;
            }
          }
        }
      } catch {
        // ignore
      }
    }

    if (serverMessage) return serverMessage;
    const raw = String(error?.message || '').trim();
    if (raw.toLowerCase().includes('invalid jwt')) {
      return '로그인 세션이 만료되었습니다. 다시 로그인 후 신청해주세요.';
    }
    if (status === 409) return '이미 신청된 전화번호입니다.';
    if (status === 404) return '유효한 초대 링크를 찾지 못했습니다.';
    if (status === 403) return '신청 권한이 없습니다. 로그인 상태를 확인해주세요.';

    if (raw && !raw.toLowerCase().includes('non-2xx')) return raw;

    if (Number.isFinite(status)) {
      return `${fallback} (status: ${status}${statusText ? ` ${statusText}` : ''})`;
    }
    return fallback;
  };

  const ensureParticipationTracking = async (responseData) => {
    const trackingStatus = String(responseData?.participationTracking?.status || '').trim();
    if (trackingStatus === 'recorded') return;

    const trackingReason = String(responseData?.participationTracking?.reason || '').trim();
    const inviteId = String(session?.id || '').trim();
    const { userId, authError } = await resolveAuthContext();

    if (String(authError || '').toLowerCase().includes('jwt')) {
      throw new Error('로그인 세션이 만료되었습니다. 다시 로그인 후 신청해주세요.');
    }

    if (!inviteId || !userId) {
      if (trackingStatus === 'failed') {
        throw new Error(
          `신청은 완료됐지만 참여 기록 저장에 실패했습니다. (${trackingReason || '알 수 없는 오류'})`
        );
      }
      if (trackingStatus === 'skipped') {
        throw new Error('신청은 완료됐지만 로그인 사용자 확인이 되지 않아 참여 중 이벤트에 반영되지 않았습니다. 다시 로그인 후 신청해주세요.');
      }
      return;
    }

    const { error: fallbackError } = await supabase
      .from('session_participants')
      .upsert(
        {
          session_invite_id: inviteId,
          user_id: userId,
          status: 'applied',
        },
        { onConflict: 'session_invite_id,user_id' }
      );

    if (fallbackError) {
      throw new Error(
        `참여 중 이벤트 연동 저장에 실패했습니다. (${fallbackError?.message || trackingReason || '알 수 없는 오류'})`
      );
    }
  };

  const handleAuthenticate = async (payload) => {
    const name = String(payload?.name || '').trim();
    const phoneNumber = String(payload?.phone || '').trim();

    if (!token) {
      throw new Error('잘못된 접근입니다.');
    }

    const participant = {
      id: globalThis.crypto?.randomUUID?.() || String(Date.now()),
      name,
      nickname: name,
      phone: phoneNumber,
      skillLevel: '',
      guestCount: '0',
      payment: 'pending',
      registeredAt: new Date().toISOString()
    };

    const { data, error } = await invokeInviteRegister({
      token,
      participant,
      mode: 'member'
    });

    if (error) {
      let errorMessage = await parseFunctionInvokeError(error, '회원 확인에 실패했습니다.');
      if (error?.message?.includes('404') || error?.context?.status === 404) {
        errorMessage = '등록된 회원 정보가 없습니다. 신규 신청을 이용해주세요.';
      }
      throw new Error(errorMessage);
    }

    if (!data?.ok) {
      const errorMsg = data?.error || '회원 확인에 실패했습니다.';
      if (errorMsg.includes('찾지 못했') || errorMsg.includes('not found')) {
        throw new Error('등록된 회원 정보가 없습니다. 신규 신청을 이용해주세요.');
      }
      throw new Error(errorMsg);
    }

    await ensureParticipationTracking(data);

    const nextList = normalizeInviteParticipants(data?.participants);
    setParticipants(nextList);

    const nextCounts = computeCounts(nextList);
    setSession((s) => ({
      ...(s || {}),
      confirmed: nextCounts.confirmed,
      waitlist: nextCounts.waitlist
    }));

    setShowAuthModal(false);
    handleOpenCompletionModal({
      type: 'existing_member',
      name,
      phone: phoneNumber
    });
  };

  const handleRegistrationSubmit = async (formData) => {
    await new Promise(resolve => setTimeout(resolve, 300));

    const newParticipant = {
      id: globalThis.crypto?.randomUUID?.() || String(Date.now()),
      name: formData?.name || '',
      nickname: formData?.name || '',
      phone: formData?.phone || '',
      skillLevel: formData?.skillLevel || '',
      guestCount: formData?.guestCount ?? '0',
      payment: 'pending',
      registeredAt: new Date().toISOString()
    };

    try {
      if (token) {
        const { data, error } = await invokeInviteRegister({
          token,
          participant: newParticipant
        });

        if (error) {
          const msg = await parseFunctionInvokeError(error, '서버 저장에 실패했습니다.');
          throw new Error(msg);
        }

        if (!data?.ok) {
          throw new Error(data?.error || '신청 처리에 실패했습니다.');
        }

        await ensureParticipationTracking(data);

        const nextList = normalizeInviteParticipants(data?.participants);
        if (nextList?.length > 0) {
          setParticipants(nextList);

          const nextCounts = computeCounts(nextList);
          setSession(s => ({
            ...(s || {}),
            confirmed: nextCounts.confirmed,
            waitlist: nextCounts.waitlist
          }));
        }
      }
    } catch (err) {
      alert(err?.message || '신청 처리에 실패했습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    setShowNewRegistrationModal(false);
    handleOpenCompletionModal({
      type: 'new_registration',
      name: formData?.name,
      phone: formData?.phone,
    });
  };

  const displayParticipants = useMemo(() => {
    return (participants || [])
      .filter(p => p?.status !== 'cancelled')
      .map(p => {
      const rawName = String((showRealNames ? p?.name : p?.nickname) || '').trim();
      const maskedName = rawName ? `${rawName.slice(0, 1)}**` : '';
      return {
        ...p,
        name: maskedName
      };
    });
  }, [participants, showRealNames]);

  const sessionUrl = useMemo(() => {
    if (!session?.token) return '';
    return `${window.location?.origin}/public-session-page?token=${session?.token}`;
  }, [session?.token]);

  const eventType = String(session?.eventType || '').trim().toLowerCase();
  const applyTitle = eventType === 'lesson' ? '레슨 신청' : eventType === 'training' ? '트레이닝 신청' : '참가신청';
  const applyCtaText = eventType === 'lesson'
    ? '레슨 신청하기'
    : eventType === 'training'
      ? '트레이닝 신청하기'
      : '신청하기';

  const seoTypeLabel = useMemo(() => {
    if (eventType === 'lesson') return '피클볼 레슨';
    if (eventType === 'training') return '피클볼 트레이닝';
    return '피클볼 오픈 게임';
  }, [eventType]);

  const formattedDateText = useMemo(() => {
    if (!session?.date) return '';
    try {
      const date = new Date(session.date);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'short',
      });
    } catch {
      return '';
    }
  }, [session?.date]);

  const seoTitle = useMemo(() => {
    const base = String(session?.title || '').trim();
    const headline = base ? `${base} | ${seoTypeLabel}` : seoTypeLabel;
    return `${headline} - Pickling`;
  }, [session?.title, seoTypeLabel]);

  const seoDescription = useMemo(() => {
    const lines = [];
    lines.push(`${seoTypeLabel} 참가 신청 페이지입니다.`);

    const venue = String(session?.venue || session?.location || '').trim();
    const time = [String(session?.startTime || '').trim(), String(session?.endTime || '').trim()].filter(Boolean).join(' - ');
    const when = [formattedDateText, time].filter(Boolean).join(' / ');

    if (when) lines.push(`일정: ${when}`);
    if (venue) lines.push(`장소: ${venue}`);

    const detail = String(session?.description || '').trim();
    if (detail) lines.push(detail);

    return lines.join('\n');
  }, [formattedDateText, seoTypeLabel, session?.description, session?.endTime, session?.location, session?.startTime, session?.venue]);

  const seoImageUrl = useMemo(() => {
    const cover = String(session?.coverImageUrl || '').trim();
    if (cover) return cover;
    const urls = Array.isArray(session?.imageUrls) ? session.imageUrls : [];
    return String(urls?.[0] || '').trim();
  }, [session?.coverImageUrl, session?.imageUrls]);

  const structuredData = useMemo(() => {
    const url = sessionUrl;
    const name = String(session?.title || '').trim() || seoTypeLabel;
    const description = String(session?.description || '').trim() || seoDescription;

    const type = eventType === 'lesson' ? 'EducationEvent' : 'SportsEvent';

    const locationName = String(session?.venue || session?.location || '').trim();
    const addressText = String(session?.venueAddress || '').trim();

    const startDate = session?.date ? new Date(session.date).toISOString() : undefined;

    return {
      '@context': 'https://schema.org',
      '@type': type,
      name,
      description,
      url,
      image: seoImageUrl ? [seoImageUrl] : undefined,
      startDate,
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      location: locationName
        ? {
            '@type': 'Place',
            name: locationName,
            address: addressText || undefined,
          }
        : undefined,
      organizer: {
        '@type': 'Organization',
        name: 'Pickling',
        url: String(window.location?.origin || ''),
      },
    };
  }, [eventType, seoDescription, seoImageUrl, seoTypeLabel, session?.date, session?.description, session?.location, session?.title, session?.venue, session?.venueAddress, sessionUrl]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-base md:text-lg text-muted-foreground">세션 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card border border-border rounded-xl p-6 text-center">
          <Icon name="AlertCircle" size={40} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-base font-medium text-foreground mb-1">세션을 불러올 수 없습니다</p>
          <p className="text-sm caption text-muted-foreground">{loadError}</p>
          <div className="mt-4">
            <Button variant="outline" onClick={() => navigate('/')} fullWidth>
              홈으로
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card border border-border rounded-xl p-6 text-center">
          <p className="text-base font-medium text-foreground">세션 정보가 없습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        {sessionUrl && <link rel="canonical" href={sessionUrl} />}

        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        {sessionUrl && <meta property="og:url" content={sessionUrl} />}
        {seoImageUrl && <meta property="og:image" content={seoImageUrl} />}

        <meta name="twitter:card" content={seoImageUrl ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {seoImageUrl && <meta name="twitter:image" content={seoImageUrl} />}

        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      {!isEmbedded && (
        <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center">
                <PicklingLogoMark size={24} className="text-primary" />
              </div>
              <span className="text-lg md:text-xl font-heading font-semibold text-foreground">
                Pickling
              </span>
            </div>
          </div>
        </header>
      )}
      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        <div className="space-y-6 md:space-y-8">
          {sessionImages.length > 0 && (
            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-md">
              <div className="relative aspect-[16/9] bg-muted">
                <img
                  src={sessionImages[Math.min(activeSlideIndex, sessionImages.length - 1)]}
                  alt="session"
                  className="w-full h-full object-cover"
                />
                {sessionImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 border border-white/30 shadow-elevation-3 flex items-center justify-center text-white hover:bg-black/70"
                      onClick={() => setActiveSlideIndex((i) => (i - 1 + sessionImages.length) % sessionImages.length)}
                      aria-label="이전 이미지"
                    >
                      <Icon name="ChevronLeft" size={26} />
                    </button>
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 border border-white/30 shadow-elevation-3 flex items-center justify-center text-white hover:bg-black/70"
                      onClick={() => setActiveSlideIndex((i) => (i + 1) % sessionImages.length)}
                      aria-label="다음 이미지"
                    >
                      <Icon name="ChevronRight" size={26} />
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          <SessionInfoCard session={session} />

          {session?.registrationDeadline && (
            <CountdownTimer deadline={session?.registrationDeadline} />
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-6 md:space-y-8">
              <CapacityIndicator
                confirmed={session?.confirmed || 0}
                waitlist={session?.waitlist || 0}
                capacity={session?.capacity || 0}
              />

              <div className="bg-card border border-border rounded-xl p-4 md:p-6 lg:p-8 shadow-md">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                    참가자 목록
                  </h3>
                </div>

                <ParticipantTable
                  participants={displayParticipants}
                  showActions={false}
                  showFilters={false}
                  columns={['name', 'status', 'payment']}
                />
              </div>

              {session?.id && (
                <div id="public-session-inquiry">
                  <CommentSection sessionInviteId={session.id} isAdmin={false} />
                </div>
              )}
            </div>

            <div className="space-y-6 md:space-y-8">
              <div className="hidden lg:block bg-card border border-border rounded-xl p-4 md:p-6 lg:p-8 shadow-md">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">{applyTitle}</h3>
                    <p className="text-sm caption text-muted-foreground mt-1">신청 정보를 입력하고 참가 절차를 진행하세요</p>
                  </div>
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="CheckCircle" size={20} color="var(--color-primary)" />
                  </div>
                </div>

                <div className="mt-5">
                  <Button
                    variant="default"
                    iconName="CheckCircle"
                    iconPosition="right"
                    onClick={handleOpenRegistration}
                    fullWidth
                  >
                    {applyCtaText}
                  </Button>
                </div>
              </div>

              {paymentInfo && <PaymentInfoCard paymentInfo={paymentInfo} />}

              <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-md">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-base font-heading font-semibold text-foreground">신청취소</h3>
                    <p className="text-sm caption text-muted-foreground mt-1">이름과 전화번호로 신청을 취소할 수 있습니다.</p>
                  </div>
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="XCircle" size={20} color="var(--color-primary)" />
                  </div>
                </div>

                <div className="mt-4">
                  <Button variant="outline" onClick={handleOpenCancelModal} fullWidth>
                    신청취소
                  </Button>
                </div>
              </div>

              <ShareButton
                sessionUrl={sessionUrl}
                sessionTitle={session?.title}
              />
            </div>
          </div>
        </div>
      </main>

      <div className="fixed inset-x-0 bottom-0 z-200 bg-background/95 backdrop-blur border-t border-border p-4 lg:hidden">
        <Button
          variant="default"
          iconName="CheckCircle"
          iconPosition="right"
          onClick={handleOpenRegistration}
          fullWidth
        >
          {applyCtaText}
        </Button>
      </div>
      {!isEmbedded && (
        <footer className="bg-card border-t border-border mt-12 md:mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-md flex items-center justify-center">
                  <PicklingLogoMark size={20} className="text-primary" />
                </div>
                <span className="text-base font-heading font-semibold text-foreground">
                  Pickling
                </span>
              </div>
              <p className="text-sm caption text-muted-foreground text-center md:text-right">
                © {new Date()?.getFullYear()} Pickling. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      )}
      <AuthenticationModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthenticate={handleAuthenticate}
        title="회원 빠른 인증"
        description="등록된 전화번호를 입력하세요"
      />

      {showNewRegistrationModal && (
        <div
          className="fixed inset-0 z-300 flex items-start justify-center pt-[6vh] px-4"
          onClick={(e) => {
            if (e?.target === e?.currentTarget) handleCloseNewRegistrationModal();
          }}
        >
          <div className="fixed inset-0 bg-background/70 backdrop-blur-sm z-10" aria-hidden="true" />
          <div className="relative z-20 w-full max-w-xl max-h-[88vh] bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
            <div className="bg-card border-b border-border p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-heading font-semibold text-foreground">{applyTitle}</h2>
                <p className="text-xs caption text-muted-foreground mt-0.5">신규 신청 정보를 입력해주세요</p>
              </div>
              <button
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic active-press"
                onClick={handleCloseNewRegistrationModal}
                aria-label="닫기"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="max-h-[calc(88vh-64px)] overflow-y-auto">
              <div className="p-4">
                <RegistrationForm
                  onSubmit={handleRegistrationSubmit}
                  onQuickAuth={() => {
                    setShowNewRegistrationModal(false);
                    setShowAuthModal(true);
                  }}
                  isFull={false}
                  session={session}
                  paymentInfo={paymentInfo}
                  useFixedSubmitBar={false}
                  useCardWrapper={false}
                  showQuickAuthSection={false}
                  showTitle={false}
                  useStickySubmitBarOnMobile={true}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {showCompletionModal && (
        <div
          className="fixed inset-0 z-300 flex items-start justify-center pt-[6vh] px-4"
          onClick={(e) => {
            if (e?.target === e?.currentTarget) handleCloseCompletionModal();
          }}
        >
          <div className="fixed inset-0 bg-background/70 backdrop-blur-sm z-10" aria-hidden="true" />
          <div className="relative z-20 w-full max-w-md bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
            <div className="bg-card border-b border-border p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-heading font-semibold text-foreground">신청 완료</h2>
                <p className="text-xs caption text-muted-foreground mt-0.5">입금 안내를 확인해주세요</p>
              </div>
              <button
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic active-press"
                onClick={handleCloseCompletionModal}
                aria-label="닫기"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {(completionInfo?.name || completionInfo?.phone) && (
                <div className="bg-muted rounded-md border border-border p-3">
                  <p className="text-sm font-medium text-foreground">
                    {completionInfo?.name ? `${completionInfo?.name}님` : '신청자'} 신청이 접수되었습니다.
                  </p>
                  {completionInfo?.phone && (
                    <p className="text-xs caption text-muted-foreground mt-1">전화번호: {completionInfo?.phone}</p>
                  )}
                </div>
              )}

              <div className="bg-primary/10 border border-primary/20 rounded-md p-3">
                <div className="flex items-start gap-2">
                  <Icon name="Info" size={18} className="text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">중요</p>
                    <p className="text-xs caption text-muted-foreground mt-1">
                      입금까지 완료되어야 참가가 확정됩니다.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-muted rounded-md border border-border p-3 space-y-2">
                <p className="text-sm font-heading font-medium text-foreground">입금 계좌</p>
                <div className="text-sm text-foreground data-text">
                  {paymentInfo?.bankName} {paymentInfo?.accountNumber}
                </div>
                <div className="text-xs caption text-muted-foreground">예금주: {paymentInfo?.accountHolder}</div>
                {paymentInfo?.paymentDeadline && (
                  <div className="text-xs caption text-muted-foreground">
                    입금 마감: {new Date(paymentInfo?.paymentDeadline)?.toLocaleString('ko-KR')}
                  </div>
                )}
                {paymentInfo?.notes && (
                  <pre className="text-xs caption text-muted-foreground whitespace-pre-wrap leading-relaxed pt-2 border-t border-border">
                    {paymentInfo?.notes}
                  </pre>
                )}
              </div>

              <Button
                variant="default"
                iconName="CheckCircle"
                iconPosition="right"
                onClick={handleCloseCompletionModal}
                fullWidth
              >
                확인
              </Button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div
          className="fixed inset-0 z-300 flex items-start justify-center pt-[6vh] px-4"
          onClick={(e) => {
            if (e?.target === e?.currentTarget) setShowCancelModal(false);
          }}
        >
          <div className="fixed inset-0 bg-background/70 backdrop-blur-sm z-10" aria-hidden="true" />
          <div className="relative z-20 w-full max-w-md bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
            <div className="bg-card border-b border-border p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-heading font-semibold text-foreground">신청취소</h2>
                <p className="text-xs caption text-muted-foreground mt-0.5">전화번호와 취소 코드를 입력하세요</p>
              </div>
              <button
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic active-press"
                onClick={() => setShowCancelModal(false)}
                aria-label="닫기"
                type="button"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleCancelSubmit} className="p-4 space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">이름</label>
                <input
                  value={cancelForm.name}
                  onChange={(e) => setCancelForm((s) => ({ ...s, name: e.target.value }))}
                  placeholder="홍길동"
                  className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">전화번호</label>
                <input
                  value={cancelForm.phone}
                  onChange={(e) => setCancelForm((s) => ({ ...s, phone: e.target.value }))}
                  placeholder="01012345678"
                  className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground"
                />
              </div>

              <Button variant="warning" fullWidth disabled={isCancelling}>
                {isCancelling ? '취소 처리중...' : '신청취소'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicSessionPage;